import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SplashPage } from '@/pages/SplashPage';
import { HomePage } from '@/pages/HomePage';
import { useAuthStore } from '@/stores/auth.store';

// Code splitting: HomePage + SplashPage stay in the main chunk (first paint
// after auth is the critical path). Everything else loads on demand so the
// initial bundle drops from ~600 KB to ~250 KB.
const CategoryDetailPage = lazy(() =>
  import('@/pages/CategoryDetailPage').then((m) => ({ default: m.CategoryDetailPage })),
);
const ProductDetailPage = lazy(() =>
  import('@/pages/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })),
);
const CheckoutPage = lazy(() =>
  import('@/pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })),
);
const PaymentPage = lazy(() =>
  import('@/pages/PaymentPage').then((m) => ({ default: m.PaymentPage })),
);
const PaymentSuccessPage = lazy(() =>
  import('@/pages/PaymentSuccessPage').then((m) => ({ default: m.PaymentSuccessPage })),
);
const PaymentFailedPage = lazy(() =>
  import('@/pages/PaymentFailedPage').then((m) => ({ default: m.PaymentFailedPage })),
);
const OrdersPage = lazy(() =>
  import('@/pages/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetailPage = lazy(() =>
  import('@/pages/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SupportPage = lazy(() =>
  import('@/pages/SupportPage').then((m) => ({ default: m.SupportPage })),
);
const StatsPage = lazy(() =>
  import('@/pages/StatsPage').then((m) => ({ default: m.StatsPage })),
);
const LanguagePage = lazy(() =>
  import('@/pages/LanguagePage').then((m) => ({ default: m.LanguagePage })),
);
const PersonalInfoPage = lazy(() =>
  import('@/pages/PersonalInfoPage').then((m) => ({ default: m.PersonalInfoPage })),
);
const SecurityPage = lazy(() =>
  import('@/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const NotificationsPage = lazy(() =>
  import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const RulesPage = lazy(() =>
  import('@/pages/RulesPage').then((m) => ({ default: m.RulesPage })),
);
const FaqPage = lazy(() => import('@/pages/FaqPage').then((m) => ({ default: m.FaqPage })));
const SearchPage = lazy(() =>
  import('@/pages/SearchPage').then((m) => ({ default: m.SearchPage })),
);
const FavoritesPage = lazy(() =>
  import('@/pages/FavoritesPage').then((m) => ({ default: m.FavoritesPage })),
);
const TrustPage = lazy(() =>
  import('@/pages/TrustPage').then((m) => ({ default: m.TrustPage })),
);

function PrivateRoute({ children }: { children: JSX.Element }) {
  const token = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  // Forward the original location so SplashPage can route us back after login.
  // Without this, bot deep links like /category/<slug> get swallowed and the
  // user always lands on /home.
  return token ? children : <Navigate to="/" replace state={{ from: location }} />;
}

/**
 * Minimal fallback while a route chunk loads — uses the same dark gradient
 * as the rest of the app so there's no white flash between pages.
 */
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary">
      <div className="flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-2 w-2 rounded-full bg-primary opacity-60"
            style={{
              animation: `pulse 1s ${i * 0.15}s ease-in-out infinite alternate`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<SplashPage />} />
        <Route path="/home" element={<PrivateRoute><HomePage /></PrivateRoute>} />
        <Route path="/category/:slug" element={<PrivateRoute><CategoryDetailPage /></PrivateRoute>} />
        <Route path="/product/:id" element={<PrivateRoute><ProductDetailPage /></PrivateRoute>} />
        <Route path="/checkout/:productId" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
        <Route path="/payment/:orderId" element={<PrivateRoute><PaymentPage /></PrivateRoute>} />
        <Route path="/payment/success/:orderId" element={<PrivateRoute><PaymentSuccessPage /></PrivateRoute>} />
        <Route path="/payment/failed/:orderId" element={<PrivateRoute><PaymentFailedPage /></PrivateRoute>} />
        <Route path="/orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
        <Route path="/order/:id" element={<PrivateRoute><OrderDetailPage /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
        <Route path="/profile/language" element={<PrivateRoute><LanguagePage /></PrivateRoute>} />
        <Route path="/profile/info" element={<PrivateRoute><PersonalInfoPage /></PrivateRoute>} />
        <Route path="/profile/security" element={<PrivateRoute><SecurityPage /></PrivateRoute>} />
        <Route path="/profile/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
        <Route path="/profile/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        <Route path="/profile/rules" element={<PrivateRoute><RulesPage /></PrivateRoute>} />
        <Route path="/support" element={<PrivateRoute><SupportPage /></PrivateRoute>} />
        <Route path="/stats" element={<PrivateRoute><StatsPage /></PrivateRoute>} />
        <Route path="/faq" element={<PrivateRoute><FaqPage /></PrivateRoute>} />
        <Route path="/search" element={<PrivateRoute><SearchPage /></PrivateRoute>} />
        <Route path="/favorites" element={<PrivateRoute><FavoritesPage /></PrivateRoute>} />
        <Route path="/trust" element={<PrivateRoute><TrustPage /></PrivateRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
